import RestClient from '../integration/RestClient';
import { ApiAutoCompleteAllPostRequest, ApiCommonGeographyBuildRuleFormDataGeoGetRequest } from '../integrated-client/apis';
import { AutoCompleteType, BrandAndMarketListItem, GuidIdAndLabel, GuidIdCodeAndLabel, RuleRouteRestrictionPointType, StringIdAndLabel } from '../integrated-client/models';

export enum DEFAULT_PATHS {
  SEARCHES_MANAGER = 'searches-manager',
  SEARCHES_MANAGER_V2 = 'searches-manager-new',
  BOOKING_CREATE_MANAGER = 'booking-create-manager',
  BOOKING_CREATE_MANAGER_V2 = 'booking-create-manager-new',
  BUSINESS_DATA = 'business-data',
  PROVIDER_PREFERENCE = 'provider-preference',
  PROVIDER_CREDENTIALS = 'provider-credentials',
  PROVIDER_CREDENTIALS_V2 = 'provider-credentials-new',
  IDENTITY_PROVIDER_SETTINGS = 'identity-provider-settings',
  IDENTITY_SETTINGS = 'identity-settings',
  MARKUPS = 'markups-data',
  MARKUPS_V2 = 'markups-new',
  TICKET_TLO = 'ticket-tlo',
  TICKET_TLO_V2 = 'ticket-tlo-new',
  GLOBAL_FILTERS = 'global-filters',
  GLOBAL_FILTERS_V2 = 'global-filters-new',
  SHOPPING_DIAGNOSTICS = 'shopping-diagnostics',
  SHOPPING_DIAGNOSTICS_V2 = 'shopping-diagnostics-new',
  USER_PERMISSIONS = 'user-permission',
  ACTIVITY_LOG = 'activity-log',
  TECHNICAL_LOGS = 'technical-logs',
  PROVIDERS = 'suppliers',
  ANCILLARY_SUPPLIER = 'ancillaries-mapping',
  ANCILLARY_MANAGER = 'ancillaries-manager',
  ANCILLARY_DISTRIBUTION = 'ancillary-distribution',
  VCC_CREDENTIALS = 'vcc-credentials',
  BOOKING_CREATE_MANAGER_SSR = 'booking-create-manager/ssr',
  BOOKING_TOLERANCES = 'booking-tolerances',
  REMARKS = 'remarks',
  SSR = 'ssr',
  CITIES = 'cities',
  COUNTRIES = 'countries',
  STATIONS = 'stations',
  AIRLINES = 'airlines',
  COMMERCIAL_CONTROLS = 'commercial-controls',
  BRANDS = 'brands',
  MARKETS = 'markets',
  DISTRIBUTION_CHANNELS = 'distribution-channels',
  AGENCIES = 'agencies',
  AGENCY_GROUPS = 'agency-groups',
  GEO_GROUPS = 'geo-groups',
  DIAGNOSTICS = 'diagnostics',
  VCC_CREDENTIALS_CONFERMA = 'barclaycard-conferma',
  VCC_CREDENTIALS_WEX = 'wex',
  MASTER_DATA_SETUP = 'master-data-setup',
}

type RegularExtraParamsFetch = {
  showNonEffective?: boolean;
  brandsAndMarkets?: Array<BrandAndMarketListItem> | null;
  agencyGroup?: GuidIdAndLabel | null;
  strictBrandAndMarket?: boolean;
  withGroups?: boolean;
};

type StationLocationsExtraParamsFetch = RegularExtraParamsFetch & {
  showCountry?: boolean;
};

type AirlinesExtraParamsFetch = RegularExtraParamsFetch & {
  supplier?: GuidIdAndLabel;
  isAncillaryDistribution?: boolean;
};

type UserPermissionsBrandAndMarketParamsFetch = {

};

export const SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM = { showNonEffective: true };
export const SHOW_NON_EFFECTIVE_FALSE = { showNonEffective: false };
export const SHOW_NON_EFFECTIVE_AND_COUNTRY_TRUE_EXTRA_PARAM = {
  showNonEffective: true,
  showCountry: true,
};
export const DEFAULT_AUTOCOMPLETE_LIMIT = 10;
export const USERPERMISSIONS_BRAND_MARKET_AUTOCOMPLETE_LIMIT = 55;

export const fetchAirlines = async (value: string, extraParams: AirlinesExtraParamsFetch = {}) => {
  const { showNonEffective = false, supplier, isAncillaryDistribution } = extraParams;

  try {
    return (
      isAncillaryDistribution
        ? await RestClient.airlineAutoCompleteForAncillaryDistribution.apiAncillaryDistributionCarrierBySupplierAutocompletePost({
            ancillaryDistributionCarrierBySupplierFilter: {
              supplier,
              term: value,
              limit: DEFAULT_AUTOCOMPLETE_LIMIT,
            },
          })
        : await RestClient.airlineAutocomplete.apiAirlineAutocompletePost({
            filterWithShowNonEffective: {
              term: value,
              limit: DEFAULT_AUTOCOMPLETE_LIMIT,
              showNonEffective,
            },
          })
    ).results;
  } catch (res) {
    return [];
  }
};

export const fetchStationLocations = async (value: string, extraParams: StationLocationsExtraParamsFetch = {}) => {
  const { showNonEffective = false, showCountry = false } = extraParams;
  return (
    await RestClient.stationLocationAutocompleteApi.apiStationLocationAutocompletePost({
      stationOrLocationAutocompleteFilter: {
        term: value,
        limit: DEFAULT_AUTOCOMPLETE_LIMIT,
        showNonEffective,
        showCountry,
      },
    })
  ).results;
};

export const fetchUserPermissionsBrandAndMarket = async (value: string, extraParams: UserPermissionsBrandAndMarketParamsFetch = {}) => {
  const { } = extraParams;
  return (
    await RestClient.userPermissionsBrandAndMarketAutoCompleteApi.apiUserPermissionsBrandAndMarketAutoCompletePost({
      userPermissionsBrandAndMarketFilter: {
        term: value,
        limit: USERPERMISSIONS_BRAND_MARKET_AUTOCOMPLETE_LIMIT,
      },
    })
  ).results;
};

export const fetchLocations = async (value: string, extraParams: RegularExtraParamsFetch = {}) => {
  const { showNonEffective = false } = extraParams;
  return (
    await RestClient.locationAutocompleteApi.apiLocationAutocompletePost({
      filterWithShowNonEffective: {
        term: value,
        limit: DEFAULT_AUTOCOMPLETE_LIMIT,
        showNonEffective,
      },
    })
  ).results;
};

export const fetchCountries = async (value: string, filterType: GuidIdCodeAndLabel, extraParams: RegularExtraParamsFetch = {}) => {
  const { showNonEffective = false } = extraParams;
  return (
    await RestClient.countryAutocomplete.apiCountryAutocompletePost({
      filterWithShowNonEffective: {
        term: value,
        limit: DEFAULT_AUTOCOMPLETE_LIMIT,
        showNonEffective,
      },
    })
  ).results;
};

export const fetchAgencies = async (value: string, extraParams: RegularExtraParamsFetch = {}) => {
  const { showNonEffective = false, brandsAndMarkets, agencyGroup, strictBrandAndMarket = false } = extraParams;

  return (
    await RestClient.agencyAutocomplete.apiAgencyAutocompletePost({
      agencyAutocompleteFilter: {
        term: value,
        limit: DEFAULT_AUTOCOMPLETE_LIMIT,
        agencyGroup: agencyGroup?.id,
        brandsAndMarkets,
        showNonEffective,
        strictBrandAndMarket,
      },
    })
  ).results;
};

export const fetchAutoCompleteEntities = async (
  value: string,
  type: AutoCompleteType,
  filterType: GuidIdCodeAndLabel,
  extraParams: RegularExtraParamsFetch = {}
) => {
  const { showNonEffective = false } = extraParams;
  var result = await RestClient.autoCompleteV2.apiAutoCompletePost({
    autoCompleteFilter: {
      term: value,
      type: type,
      limit: DEFAULT_AUTOCOMPLETE_LIMIT,
      showNonEffective,
      filterType: filterType,
    },
  });
  return result;
};

export const fetchAutoCompleteAllEntities = async (entity: ApiAutoCompleteAllPostRequest) => {
  return await RestClient.autoCompleteV2.apiAutoCompleteAllPost(entity);
};

export const transformAutocompleteDataV2 = (data: GuidIdCodeAndLabel[]) => {
  if (data && data.length) {
    return data.map((dataItem: GuidIdCodeAndLabel) => ({
      value: JSON.stringify(dataItem),
      label: `(${dataItem.code}) ${dataItem.label}`,
    }));
  }
  return data;
};

export const getFullGeoObjFromServer = async (code: string, pointType?: RuleRouteRestrictionPointType) => {
  const requestObj: ApiCommonGeographyBuildRuleFormDataGeoGetRequest = { code };
  if (pointType) {
    requestObj.pointType = pointType;
  }
  return await RestClient.geography.apiCommonGeographyBuildRuleFormDataGeoGet(requestObj);
};

export const fetchRegionFormConfiguration = async () => {
  return await RestClient.geography.apiGeographyGetRegionFormConfigurationGet();
};

export const fetchCountryFormConfiguration = async () => {
  return await RestClient.geography.apiGeographyGetCountryFormConfigurationGet();
};

export const fetchWrapper = (fetchFunction: (value: string, extraParams?: { [key: string]: any }) => Promise<any>, extraParams?: { [key: string]: any }) => {
  return async (value: string) => await fetchFunction(value, extraParams);
};
